import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Menu,
  ChevronLeft,
  Eye,
  FileText,
  EyeOff,
  Save,
  RotateCcw,
  Link,
  Unlink,
  Edit,
} from 'lucide-react'
import { useState, useMemo } from 'react'
import { api } from '../services/api'
import usePageBuilderStore from '../stores/pageBuilderStore'
import { useLanguageStore } from '../stores/languageStore'
import { usePageBuilderActions } from '../hooks/usePageBuilderActions'
import { useContentMetaStore } from '../stores/contentMetaStore'
import Button from './Button'
import Input from './Input'
import LanguageDropdown from './LanguageDropdown'

export default function PageBuilderHeader({ onMenuClick, pageId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const path = window.location.pathname
  const isPageBuilder = path.startsWith('/pages/')
  const isPostBuilder = path.startsWith('/posts/')
  const isProductBuilder = path.startsWith('/products/')
  const isTemplateBuilder = path.startsWith('/templates/')

  const routeConfig = isPostBuilder
    ? {
        entityType: 'post',
        listPath: '/posts',
        apiBase: '/posts',
        listQueryKey: 'posts',
        label: 'Post',
        titleField: 'title',
        slugField: 'slug',
        previewable: true,
      }
    : isProductBuilder
      ? {
          entityType: 'product',
          listPath: '/products',
          apiBase: '/ecommerce/products',
          listQueryKey: 'products',
          label: 'Product',
          titleField: 'name',
          slugField: 'slug',
          previewable: false,
        }
      : isTemplateBuilder
        ? {
            entityType: 'template',
            listPath: '/templates',
            apiBase: '/content-templates',
            listQueryKey: 'content-templates',
            label: 'Template',
            titleField: 'name',
            slugField: null,
            previewable: false,
          }
        : {
            entityType: 'page',
            listPath: '/pages',
            apiBase: '/pages',
            listQueryKey: 'pages',
            label: 'Page',
            titleField: 'title',
            slugField: 'slug',
            previewable: true,
          }

  const entityType = routeConfig.entityType
  const useMetaStore = useContentMetaStore(entityType)

  const metaSaveHandler = useMetaStore((state) => state.saveHandler)
  const metaSaveDraftHandler = useMetaStore((state) => state.saveDraftHandler)
  const metaPublishHandler = useMetaStore((state) => state.publishHandler)
  const metaDiscardDraftHandler = useMetaStore(
    (state) => state.discardDraftHandler,
  )
  const isMetaSaving = useMetaStore((state) => state.isSaving)
  const isMetaPublishing = useMetaStore((state) => state.isPublishing)
  const isMetaDiscarding = useMetaStore((state) => state.isDiscarding)

  const id =
    pageId ||
    (() => {
      const pathParts = window.location.pathname.split('/')
      const builderIndex = pathParts.findIndex(
        (part) =>
          part === 'pages' ||
          part === 'posts' ||
          part === 'products' ||
          part === 'templates',
      )
      return builderIndex !== -1 && pathParts[builderIndex + 1]
        ? pathParts[builderIndex + 1]
        : undefined
    })()

  const isNew = id === 'new'
  const viewMode = usePageBuilderStore((state) => state.viewMode)
  const setViewMode = usePageBuilderStore((state) => state.setViewMode)
  const hasUnsavedChanges = usePageBuilderStore(
    (state) => state.hasUnsavedChanges,
  )
  const hasDraftStates = usePageBuilderStore((state) => state.hasDraftStates)
  const { currentLanguageCode: locale } = useLanguageStore()
  const [isDiscarding, setIsDiscarding] = useState(false)
  const [isEditingPageInfo, setIsEditingPageInfo] = useState(false)
  const [editedTitle, setEditedTitle] = useState('')
  const [editedSlug, setEditedSlug] = useState('')

  const {
    handleSaveAll,
    handlePublishAll,
    handleDiscardDraft,
    isSaving,
    isPublishing,
  } = usePageBuilderActions(id)

  const entityTypePlural =
    routeConfig.entityType === 'template'
      ? 'templates'
      : `${routeConfig.entityType}s`
  const entityLabel = routeConfig.label

  const updateSyncBlocksMutation = useMutation({
    mutationFn: async (syncBlocks) => {
      const response = await api.put(`${routeConfig.apiBase}/${id}`, {
        sync_blocks: syncBlocks,
        locale: locale,
      })
      return response.data
    },
    onMutate: async (syncBlocks) => {
      await queryClient.cancelQueries({ queryKey: [entityType, id, locale] })
      const previousData = queryClient.getQueryData([entityType, id, locale])

      queryClient.setQueryData([entityType, id, locale], (old) => {
        if (!old) return old
        return {
          ...old,
          data: {
            ...old.data,
            sync_blocks: syncBlocks,
          },
        }
      })

      return { previousData }
    },
    onError: (err, syncBlocks, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([entityType, id, locale], context.previousData)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [entityType, id] })
    },
  })

  const updatePageInfoMutation = useMutation({
    mutationFn: ({ title, slug }) =>
      api.put(`${routeConfig.apiBase}/${id}`, {
        ...(routeConfig.entityType === 'template'
          ? { name: title }
          : routeConfig.entityType === 'product'
            ? { name: title, slug }
            : { title, slug }),
        locale: locale,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([entityType, id])
      queryClient.invalidateQueries([routeConfig.listQueryKey])
      setIsEditingPageInfo(false)
    },
    onError: (error) => {
      alert(
        error.response?.data?.message ||
          `Failed to update ${entityLabel.toLowerCase()} information`,
      )
    },
  })

  const {
    data: pageRes,
    isLoading: pageLoading,
    error: pageError,
  } = useQuery({
    queryKey: [entityType, id, locale],
    queryFn: () =>
      api
        .get(`${routeConfig.apiBase}/${id}`, {
          params: { locale },
        })
        .then((r) => r.data),
    enabled: !!id && !isNew,
  })

  const page = pageRes?.data || null

  const pageStatus = useMemo(() => {
    if (!page) return null

    if (routeConfig.entityType === 'template') {
      return page.is_active
        ? {
            status: 'active',
            label: 'Active',
            color: 'bg-green-100 text-green-800',
          }
        : {
            status: 'inactive',
            label: 'Inactive',
            color: 'bg-gray-100 text-gray-800',
          }
    }

    const statusValue =
      page.status || (page.is_published ? 'published' : 'draft')
    if (statusValue === 'published' || statusValue === 'active') {
      return {
        status: statusValue,
        label: statusValue === 'active' ? 'Active' : 'Published',
        color: 'bg-green-100 text-green-800',
      }
    }
    if (statusValue === 'archived') {
      return {
        status: 'archived',
        label: 'Archived',
        color: 'bg-gray-100 text-gray-800',
      }
    }
    return {
      status: 'draft',
      label: 'Draft',
      color: 'bg-yellow-100 text-yellow-800',
    }
  }, [page, routeConfig.entityType])

  const handleSave = async () => {
    if (metaSaveDraftHandler) {
      await metaSaveDraftHandler()
    } else {
      await handleSaveAll()
    }
  }

  const handlePublish = async () => {
    if (metaPublishHandler) {
      await metaPublishHandler()
      await queryClient.refetchQueries({
        queryKey: [entityType, id, locale],
      })
      await queryClient.refetchQueries({
        queryKey: [routeConfig.listQueryKey],
      })
    } else {
      await handlePublishAll()
    }
  }

  const handlePreview = () => {
    if (!routeConfig.previewable) return
    const previewUrl = `/${entityTypePlural}/${id}/preview?locale=${locale}&view_mode=${viewMode}`
    window.open(previewUrl, '_blank')
  }

  const handleDiscard = async () => {
    if (
      window.confirm(
        'Are you sure you want to discard all draft changes? This action cannot be undone.',
      )
    ) {
      if (metaDiscardDraftHandler) {
        await metaDiscardDraftHandler()
      } else {
        setIsDiscarding(true)
        try {
          await handleDiscardDraft()
        } finally {
          setIsDiscarding(false)
        }
      }
    }
  }

  const handleEditPageInfo = () => {
    setEditedTitle(page?.[routeConfig.titleField] || '')
    setEditedSlug(
      routeConfig.slugField ? page?.[routeConfig.slugField] || '' : '',
    )
    setIsEditingPageInfo(true)
  }

  const handleSavePageInfo = () => {
    if (!editedTitle.trim() || (routeConfig.slugField && !editedSlug.trim())) {
      alert(
        routeConfig.slugField
          ? 'Name and slug are required'
          : 'Name is required',
      )
      return
    }
    updatePageInfoMutation.mutate({
      title: editedTitle,
      slug: editedSlug,
    })
  }

  return (
    <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
      <button
        type="button"
        className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
        onClick={onMenuClick}
      >
        <Menu className="h-6 w-6" />
      </button>

      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(routeConfig.listPath)}
            className="text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="relative">
              <h1 className="text-xl font-semibold text-gray-900">
                {entityLabel} Builder
              </h1>
              <p className="text-sm text-gray-500">
                {isNew ? (
                  `Creating new ${entityLabel.toLowerCase()}...`
                ) : pageLoading ? (
                  `Loading ${entityLabel.toLowerCase()}...`
                ) : pageError ? (
                  `Error loading ${entityLabel.toLowerCase()}`
                ) : page ? (
                  <span className="flex items-center gap-2">
                    <span>{page[routeConfig.titleField]}</span>
                    {routeConfig.slugField && (
                      <span className="text-xs text-gray-400">
                        /{page[routeConfig.slugField]}
                      </span>
                    )}
                    {pageStatus && (
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${pageStatus.color}`}
                      >
                        {pageStatus.label}
                      </span>
                    )}
                    <button
                      onClick={handleEditPageInfo}
                      className="text-gray-400 hover:text-gray-600"
                      title="Edit page info"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                  </span>
                ) : (
                  'No page data'
                )}
              </p>
              {isEditingPageInfo && (
                <div className="absolute top-full left-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    Edit {entityLabel} Info - {locale.toUpperCase()}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <Input
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        placeholder={`${entityLabel} title`}
                      />
                    </div>
                    {routeConfig.slugField && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Slug
                        </label>
                        <Input
                          value={editedSlug}
                          onChange={(e) => setEditedSlug(e.target.value)}
                          placeholder={`${entityLabel.toLowerCase()}-slug`}
                        />
                      </div>
                    )}
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsEditingPageInfo(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSavePageInfo}
                        disabled={updatePageInfoMutation.isPending}
                      >
                        {updatePageInfoMutation.isPending
                          ? 'Saving...'
                          : 'Save'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {routeConfig.entityType !== 'template' && (
              <LanguageDropdown
                currentLocale={locale}
                availableLocales={page?.available_locales || []}
              />
            )}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Sync:</span>
                <button
                  onClick={() =>
                    updateSyncBlocksMutation.mutate(!page?.sync_blocks)
                  }
                  disabled={updateSyncBlocksMutation.isPending || !page}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                    page?.sync_blocks
                      ? 'bg-green-100 text-green-700 hover:bg-green-200 border-green-200'
                      : 'bg-red-100 text-red-700 hover:bg-red-200 border-red-200'
                  } ${
                    updateSyncBlocksMutation.isPending
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                  title={
                    updateSyncBlocksMutation.isPending
                      ? 'Updating...'
                      : page?.sync_blocks
                        ? 'Blocks are synced across locales. Click to disable.'
                        : 'Blocks are locale-specific. Click to enable sync.'
                  }
                >
                  {page?.sync_blocks ? (
                    <Link className="h-4 w-4" />
                  ) : (
                    <Unlink className="h-4 w-4" />
                  )}
                  <span className="font-semibold">
                    {updateSyncBlocksMutation.isPending
                      ? '...'
                      : page?.sync_blocks
                        ? 'ON'
                        : 'OFF'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <Button
              variant={viewMode === 'draft' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('draft')}
              className="flex items-center space-x-2"
            >
              <FileText className="h-4 w-4" />
              <span>Draft</span>
            </Button>
            <Button
              variant={viewMode === 'live' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('live')}
              className="flex items-center space-x-2"
            >
              <Eye className="h-4 w-4" />
              <span>Live</span>
            </Button>
          </div>
          {
            <>
              {routeConfig.previewable && (
                <Button variant="outline" size="sm" onClick={handlePreview}>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscard}
                disabled={
                  viewMode === 'live' ||
                  !hasDraftStates ||
                  isMetaDiscarding ||
                  isNew
                }
                className="flex items-center space-x-2"
              >
                <RotateCcw className="h-4 w-4" />
                <span>
                  {isMetaDiscarding ? 'Discarding...' : 'Discard Draft'}
                </span>
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={
                  viewMode === 'live' ||
                  (isNew ? false : !hasUnsavedChanges) ||
                  isMetaSaving
                }
                className="flex items-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>
                  {isMetaSaving
                    ? isNew
                      ? 'Creating...'
                      : 'Saving...'
                    : 'Save Draft'}
                </span>
              </Button>
              {!isNew && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handlePublish}
                  disabled={viewMode === 'live' || isMetaPublishing}
                  className="flex items-center space-x-2"
                >
                  <Eye className="h-4 w-4" />
                  <span>
                    {(() => {
                      const isPublishedState =
                        routeConfig.entityType === 'template'
                          ? !!page?.is_active
                          : page?.status === 'published' ||
                            page?.status === 'active'

                      if (isMetaPublishing) {
                        return isPublishedState ? 'Saving...' : 'Publishing...'
                      }
                      return isPublishedState ? 'Save' : 'Publish'
                    })()}
                  </span>
                </Button>
              )}
            </>
          }
        </div>
      </div>
    </div>
  )
}
